import { useRef, useState, useEffect } from 'react';
import './ButtonFilter.css';
import '../pages/Projects/CheckboxSelect.css';

export const ButtonFilter = ({
    fetchData, 
    data, 
    displayName, 
    confirmFn, 
    uniqueSelection, 
    allowEmpty, 
    buttonText, 
    title, 
    emptyMessage, 
    icon, 
    buttonIcon = true,
    iconAlign = 'left',
    buttonClass, 
    buttonStyle, 
    buttonTitle, 
    iconStyle, 
    searchOption,
    searchMode = 'scroll', 
    showIcons, 
    showClearFilter = true,
    showClearOptions,
    refreshFn,
    showNotifications = false,
    confirmButtons = true,
    buttonsOnClick,
    menuAlign = 'right',
}) => {

    const [menuVisible, setMenuVisible] = useState(false);
    const buttonRef = useRef(null);
    const searchInputRef = useRef(null);
    const menuRef = useRef(null);
    const ulRef = useRef(null);
    const [highlightIdx, setHighlightIdx] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [noSelection, setNoSelection] = useState(true);
    const [showSelected, setShowSelected] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [filtered, setFiltered] = useState(false);
    const [selectAllState, setSelectAllState] = useState(1);
    const [loading, setLoading] = useState(false);
    const [, setRefresh] = useState(false);
    const started = useRef(false);

    const showClearState = showClearOptions?.stateFn?.() ? 0 : 1;
    const showClearText = showClearOptions?.text?.[showClearState] ?? 'Clear Filter';
    const showClearIcon = showClearOptions?.icon?.[showClearState] ?? 'e-menu-icon e-icons e-excl-filter-icon' + (filtered ? ' e-filtered' : '');
    const showClearStyle = {
        background: showClearOptions?.background?.[showClearState],
        color: showClearOptions?.color?.[showClearState]
    };
    const mainButtonStyle = showClearOptions?.applyToButton ? showClearStyle : {};
    const showClearDisabled = showClearOptions?.disabledFn?.() ?? !filtered;

    const handleOnBlur = (e) => {
        if(!buttonRef.current.contains(e.relatedTarget)) setMenuVisible(false);
    }
    
    const saveColumns = (e) => {
        e.stopPropagation();
        data.forEach((f, idx) => {
            f.isChecked = dataSource[idx].isChecked;
        })
        confirmFn?.();
        setMenuVisible(false);
    }
    
    const handleClearFilter = (e) => {
        if(showClearDisabled) return;
        if(!showClearOptions?.actionFn) {
            data.forEach((f) => f.isChecked = true);
            confirmFn?.();
        } else {
            showClearOptions?.actionFn?.();
            setRefresh(p => !p);
        } 
        setMenuVisible(false);
    }

    const handleButtonClick = (e) => {
        if(e.ctrlKey) {
            handleClearFilter();
        } else {
            setMenuVisible(p => !p);
        } 
    }
    
    const selectAll = () => {
        if(selectAllState !== 1) {
            setDataSource(p => p.map(r => r.isChecked ? r : {...r, isChecked: true}));
        } else {
            setDataSource(p => p.map(r => !r.isChecked ? r : {...r, isChecked: false}));
        }
    }

    const buttonOnClick = (option) => {
        buttonsOnClick?.(option);
        setMenuVisible(false);
    }
    
    useEffect(() => {
        if(searchMode !== 'scroll') return;

        const callback = () => {
            const val = searchValue.toLowerCase();
            const columnsData = !showSelected ? dataSource : dataSource.filter(c => c.isChecked); 
            const idxs = !val ? [] :
                columnsData.reduce((acc, obj, index) => {
                    if (obj?.[displayName ?? 'name']?.toLowerCase().includes(val)) acc.push(index);
                    return acc;
                }, []);
                
            setHighlightIdx(idxs);
            if(idxs.length === 0) return;
            const li = ulRef.current.children[idxs[0]];
            if(!li) return;
            const offset = li.offsetTop - (ulRef.current.clientHeight / 2) + (li.clientHeight / 2);

            ulRef.current.scrollTo({
                top: offset - 35,
                behavior: "smooth"
            });
        }
        
        const timeout = setTimeout(() => callback(), 100);
        
        return () => clearTimeout(timeout);
    }, [dataSource, displayName, searchMode, searchValue, showSelected])

    useEffect(() => {
        if(!menuVisible) setSearchValue('');
        if(!menuVisible || !menuRef.current) return;

        const rect = buttonRef.current.getBoundingClientRect();
        menuRef.current.style.top = (rect.top + rect.height + 2) + 'px';
        if(menuAlign === 'right' && rect.right - menuRef.current.offsetWidth > 1) {
            menuRef.current.style.left = (rect.right - menuRef.current.offsetWidth) + 'px';
        } else {
            menuRef.current.style.left = rect.left + 'px';
        }
        searchInputRef.current?.focus();
    },[buttonRef, menuAlign, menuVisible])   
    
    useEffect(() => { setDataSource(menuVisible ? structuredClone(data ?? []) : []) }, [data, menuVisible]);
    
    useEffect(() => { 
        setNoSelection(!dataSource.some(c => c.isChecked));
        const checked = dataSource.filter(c => c.isChecked);
        setSelectAllState(checked.length === dataSource.length ? 1 : checked.length === 0 ? 0 : -1);
    }, [dataSource]);
    
    useEffect(() => { if(noSelection) setShowSelected(false) }, [noSelection]);

    useEffect(() => { 
        const updateData = () => {
            setDataSource(structuredClone(data ?? []));
            if(data.length === 0) showClearOptions?.actionFn?.();
            setLoading(false);
        }

        if(!started.current && showNotifications && fetchData) {
            fetchData(updateData, true);
            started.current = true;
        }
        if(!menuVisible) return;
        if(fetchData) {
            setLoading(true);
            fetchData(updateData);
        } else {
            setFiltered(data.some(c => !c.isChecked));
            setDataSource(structuredClone(data ?? []));
        }
        started.current = true;
    }, [data, menuVisible, fetchData, showClearOptions, showNotifications]);

    useEffect(() => {
        if(!refreshFn) return
        refreshFn.current = () => setRefresh(p => !p);
        return () => refreshFn.current = null;
    }, [refreshFn])
    
    const getIcons = (v) => {
        if(v.value === 1) return <div className='bi bi-check button-filter-icon completed' />
        if(v.value === 0) return <div className='bi bi-check button-filter-icon' />
        if(v.value === -1) return <div className='bi bi-check button-filter-icon not-recorded' />
    }

    return (
        <div 
            ref={buttonRef} 
            id='buttonFilter'
            className={buttonClass ?? `main-button`} 
            style={{...buttonStyle, ...mainButtonStyle, position: 'relative'}}
            tabIndex="-1"
            onClick={handleButtonClick} 
            title={buttonTitle ?? title}
            onBlur={handleOnBlur} 
        >
            {showNotifications && data?.length > 0 &&
                <div className="button-filter-notifications">
                    {data.length}
                </div>
            }
            {iconAlign === 'right' && (buttonText ?? null)}
            {buttonIcon && 
                <i 
                    className={icon ?? 'bi bi-funnel-fill'} 
                    style={iconStyle ?? {fontSize: '12px', margin: 'auto'}}
                />
            }
            {iconAlign === 'left' && (buttonText ?? null)}
            {(menuVisible) &&
                <div 
                    ref={menuRef}
                    key='popup' 
                    tabIndex={0}
                    className='checkbox-select-main'  
                    style={{position: 'fixed', minWidth: '180px', background: '#FFF'}}
                    onClick={(e) => e.stopPropagation()}
                    title=''
                >
                    {showClearFilter &&
                        <div className='e-contextmenu-wrapper filterclearall' style={{cursor: 'pointer', fontSize: '14px'}} onClick={handleClearFilter}>
                            <li 
                                key='clearli' 
                                className={showClearDisabled ? 'e-disabled e-menu-item' : 'e-menu-item e-focused' } 
                                style={{
                                    listStyleType: 'none', 
                                    height: '35px', 
                                    display: 'flex', 
                                    alignItems: 'center',
                                    ...showClearStyle
                                }}
                            >
                                <span 
                                    key='clearicon' 
                                    style={{margin: '3px 10px 0px 14px', fontSize: 'inherit'}} 
                                    className={showClearIcon}
                                />
                            {showClearText}
                            </li>
                        </div>
                    }
                    {<span key='title' className='button-filter-title'>{title}</span>}
                    <div key='uldiv' className='checkbox-select-ul-div'>
                        {searchOption && 
                            <div>
                                <div  
                                    key='show-selection' 
                                    className={`${!noSelection ? `btn btn-${showSelected ? 'danger' : 'primary'}` : 'noSelect'} btn-sm checkbox-select-li-showsel`}
                                    onClick={() => {if(!noSelection) setShowSelected(p => !p)}}
                                >
                                    {noSelection ? 'Nothing selected' : !showSelected ? 'Show Selected' : 'Show All'}
                                </div> 
                                <div className='checkbox-select-search-div'>
                                    <input 
                                        ref={searchInputRef}
                                        key='search' 
                                        id='search-input' 
                                        type='text' 
                                        autoComplete="off" 
                                        spellCheck="false"
                                        className='checkbox-select-search' 
                                        value={searchValue}
                                        onChange={(e) => setSearchValue(e.target.value)} 
                                        onKeyDown={(e) => e.stopPropagation()}
                                        onKeyUp={(e) => e.stopPropagation()}
                                        placeholder='Search'
                                    />
                                    <i className={`bi bi-${searchValue.length > 0 ? 'x-lg' : 'search'} checkbox-select-search-icon ${searchValue.length > 0 ? 'clear' : ''}`} title={searchValue.length > 0 ? 'Clear search' : ''} onClick={(e) => setSearchValue('')}/>
                                </div>
                            </div>  
                        } 
                        <ul 
                            ref={ulRef}
                            className='checkbox-select-ul' 
                            style={{position: 'relative', ...(!confirmButtons ? {padding: '10px 0', height: 'auto', overflow: 'visible'} : {})}}
                        >  
                            {!loading && emptyMessage && data.length === 0 && 
                                <li key='empty' style={{color: '#b43469ff', fontSize: '1.2em', margin: '1.8em 0'}}>{emptyMessage}</li>
                            } 
                            {loading && 
                                <li key='loading' style={{color: '#0378d5', fontSize: '1.2em', margin: '1.8em 0'}}>
                                    Loading
                                    <div 
                                        className="agenda-loading-spin" 
                                        style={{height: '2.6em', width: '2.6em', borderWidth: '4px', position: 'absolute', top: 'calc(50% - 1.3em)', left: 'calc(50% - 1.3em)'}}
                                    />
                                </li>
                            } 
                            {!loading && dataSource.length > 0 && !uniqueSelection &&
                                <li 
                                    key={'all'} 
                                    className={`checkbox-select-li`}
                                    onClick={selectAll}
                                    >
                                        <div className='checkbox-select-li-div' >
                                            <div className="e-checkbox-wrapper e-css e-listview-checkbox e-checkbox-left" >
                                                <span className={'e-frame e-icons' + (selectAllState === 1 ? ' e-check' : selectAllState === -1 ? ' e-stop' : '')}/>
                                            </div> 
                                            Select All
                                        </div>
                                </li>
                            }  
                            {!loading && dataSource.map((d, idx) => {
                                if(showSelected && !d.isChecked && !noSelection) return null;
                                if(searchMode === 'filter' && searchValue.length > 0 && !d[displayName].toLowerCase().includes(searchValue.toLowerCase())) return null;
                                return (
                                    <li 
                                        key={idx} 
                                        className={
                                            `${d?.type === 'button' ? 'button-select-li' : 'checkbox-select-li'}${highlightIdx.includes(idx) ? ' highlight' : ''}`
                                        }
                                        style={{background: d?.background, color: d?.color}}
                                        onClick={
                                            !d?.type ? 
                                            () => {
                                                setDataSource(p => {
                                                    const r = p[p.indexOf(d)];
                                                    const newVal = !r.isChecked;
                                                    if(uniqueSelection) p.forEach(row => row.isChecked = false);
                                                    r.isChecked = newVal;
                                                    return [...p];
                                                });
                                            } : 
                                            d.type === 'button' ? 
                                            () => buttonOnClick?.(d) : 
                                            undefined}
                                    >
                                        {!d?.type &&
                                            <div className='checkbox-select-li-div' >
                                                <div className="e-checkbox-wrapper e-css e-listview-checkbox e-checkbox-left" >
                                                    <span className={"e-frame e-icons" + (d.isChecked ? " e-check" : "")}/>
                                                </div> 
                                                {showIcons ? <div className='button-filter-icon-div'>{getIcons(d)}{d?.[displayName ?? 'name']}</div> : d?.[displayName ?? 'name']}
                                            </div>
                                        }
                                        {d?.type === 'button' &&
                                            <div className='checkbox-select-li-div'>
                                                <div className={`button-filter-icon-div button-type ${d?.icon ?? ''}`} >
                                                    {d?.[displayName ?? 'name']}
                                                </div>
                                            </div>
                                        }
                                    </li>
                                )
                            })}                       
                        </ul>
                    </div>
                    {confirmButtons &&
                        <div className='checkbox-select-footer' style={{padding: '6px'}}>
                            <button 
                                key='okbutton'
                                className='e-cc e-control e-btn e-lib e-cc_okbtn e-primary e-flat' 
                                disabled={(!dataSource.some(f => f.isChecked) && !allowEmpty) || data.length === 0}
                                type='button'
                                onClick={saveColumns}
                            >
                                OK
                            </button>
                            <button 
                                key='cancelbutton'
                                className='e-cc e-control e-btn e-lib e-flat e-cc-cnbtn' 
                                type='button'
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setMenuVisible(false);
                                }}
                            >
                                CANCEL
                            </button>
                        </div>
                    }
                </div>

            }
        </div>
    )

}